// src/pages/tender/ProjectProgress.js
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { db, storage } from "../../firebaseConfig";
import dayjs from "dayjs";
import toast, { Toaster } from "react-hot-toast";

const cx = (...xs) => xs.filter(Boolean).join(" ");

export default function ProjectProgress() {
  const { tenderId } = useParams();
  const navigate = useNavigate();
  const auth = useMemo(() => getAuth(), []);

  const [user, setUser] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [tender, setTender] = useState(null);
  const [boqItems, setBoqItems] = useState([]);
  const [logs, setLogs] = useState([]);

  const [loading, setLoading] = useState(true);

  // form
  const [logDate, setLogDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [boqItemId, setBoqItemId] = useState("");
  const [description, setDescription] = useState("");
  const [qty, setQty] = useState("");
  const [overallProgress, setOverallProgress] = useState("");
  const [photos, setPhotos] = useState([]);

  // auth guard
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        toast.error("Please login as Contractor / Vendor.");
        navigate("/tender?tab=login", { replace: true });
        return;
      }
      setUser(u);
    });
    return () => unsub();
  }, [auth, navigate]);

  // load vendor + tender + BOQ + logs
  useEffect(() => {
    const load = async () => {
      if (!user || !tenderId) return;
      try {
        setLoading(true);

        // vendor
        const vSnap = await getDoc(doc(db, "vendors", user.uid));
        if (!vSnap.exists()) {
          toast.error("Vendor profile not found.");
          navigate("/tender?tab=login", { replace: true });
          return;
        }
        setVendor({ id: vSnap.id, ...vSnap.data() });

        // tender
        const tSnap = await getDoc(doc(db, "tenders", tenderId));
        if (!tSnap.exists()) {
          toast.error("Tender not found.");
          navigate("/tender/contractor", { replace: true });
          return;
        }
        setTender({ id: tSnap.id, ...tSnap.data() });

        // BOQ items
        const boqSnap = await getDocs(
          query(
            collection(db, "tender_boq_items"),
            where("tenderId", "==", tenderId)
          )
        );
        const items = [];
        boqSnap.forEach((d) => items.push({ id: d.id, ...d.data() }));
        items.sort((a, b) => (a.itemNo || 0) - (b.itemNo || 0));
        setBoqItems(items);

        // logs
        const logSnap = await getDocs(
          query(
            collection(db, "construction_logs"),
            where("tenderId", "==", tenderId),
            where("vendorUid", "==", user.uid)
          )
        );
        const list = [];
        logSnap.forEach((d) => list.push({ id: d.id, ...d.data() }));
        list.sort((a, b) => (b.logDateSeconds || 0) - (a.logDateSeconds || 0));
        setLogs(list);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load progress data.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user, tenderId, navigate]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/tender?tab=login", { replace: true });
  };

  const handleFileChange = (e) => {
    setPhotos(Array.from(e.target.files || []));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!boqItemId) {
      toast.error("Select BOQ item");
      return;
    }
    if (!qty) {
      toast.error("Enter quantity executed");
      return;
    }
    const progNum = Number(overallProgress || 0);
    if (progNum < 0 || progNum > 100) {
      toast.error("Progress must be between 0 and 100");
      return;
    }

    const item = boqItems.find((x) => x.id === boqItemId);
    if (!item) {
      toast.error("Invalid BOQ item");
      return;
    }

    try {
      setLoading(true);

      const basePayload = {
        tenderId,
        vendorUid: user.uid,
        boqItemId,
        itemNo: item.itemNo,
        description:
          description || item.description || "BOQ item execution",
        unit: item.unit || "",
        qty: Number(qty),
        logDate,
        logDateSeconds: dayjs(logDate).unix(),
        overallProgress: progNum,
        createdAt: serverTimestamp(),
      };

      const logRef = await addDoc(
        collection(db, "construction_logs"),
        basePayload
      );

      let photoList = [];
      if (photos.length) {
        const uploads = photos.map(async (file, idx) => {
          const path = `tender_progress/${tenderId}/${logRef.id}/${Date.now()}_${idx}_${file.name}`;
          const sRef = ref(storage, path);
          await uploadBytes(sRef, file);
          const url = await getDownloadURL(sRef);
          return { url, name: file.name, path };
        });
        photoList = await Promise.all(uploads);
        await updateDoc(logRef, { photos: photoList });
      }

      // update tender overall progress (simple overwrite)
      await updateDoc(doc(db, "tenders", tenderId), {
        constructionOverallProgress: progNum,
        constructionLastUpdate: serverTimestamp(),
      });

      toast.success("Daily progress saved");

      setDescription("");
      setQty("");
      setOverallProgress("");
      setPhotos([]);

      // reload logs
      const logSnap = await getDocs(
        query(
          collection(db, "construction_logs"),
          where("tenderId", "==", tenderId),
          where("vendorUid", "==", user.uid)
        )
      );
      const list = [];
      logSnap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      list.sort(
        (a, b) => (b.logDateSeconds || 0) - (a.logDateSeconds || 0)
      );
      setLogs(list);
    } catch (e) {
      console.error(e);
      toast.error("Failed to save progress");
    } finally {
      setLoading(false);
    }
  };

  if (!user || !vendor || !tender) {
    return (
      <>
        <Toaster position="top-center" />
        <div className="min-h-screen flex items-center justify-center bg-emerald-50">
          <div className="text-sm text-slate-600">
            Loading project progress…
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Toaster position="top-center" />
      <div className="min-h-screen bg-emerald-50 pb-10">
        <div className="max-w-6xl mx-auto px-4 py-6">
          {/* HEADER */}
          <div className="rounded-2xl bg-white shadow-lg border border-emerald-200 px-6 py-5 mb-6 flex items-center justify-between">
            <div>
              <div className="text-xs text-gray-500 font-semibold uppercase tracking-wide">
                Daily Progress – BOQ Item Wise
              </div>
              <h1 className="text-xl md:text-2xl font-extrabold text-emerald-800">
                {tender.title || tender.projectName || "Project"}
              </h1>
              <div className="mt-1 text-[11px] text-slate-600">
                {tender.ownerCompany || ""} · {tender.city || ""}
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <button
                onClick={handleLogout}
                className="text-xs px-3 py-2 rounded-lg bg-gray-900 text-white shadow hover:bg-black"
              >
                Logout
              </button>
              <Link
                to="/tender/contractor"
                className="text-[11px] text-emerald-700 underline"
              >
                ← Back to Contractor Dashboard
              </Link>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* FORM */}
            <div className="rounded-2xl bg-white border border-emerald-100 p-4 shadow-sm">
              <h2 className="text-sm font-bold text-emerald-800 mb-3">
                Add Daily Progress (BOQ Item)
              </h2>
              <form onSubmit={handleSubmit} className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-600 font-semibold">
                      Date
                    </label>
                    <input
                      type="date"
                      value={logDate}
                      onChange={(e) => setLogDate(e.target.value)}
                      className="mt-1 w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-600 font-semibold">
                      BOQ Item
                    </label>
                    <select
                      value={boqItemId}
                      onChange={(e) => setBoqItemId(e.target.value)}
                      className="mt-1 w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="">Select…</option>
                      {boqItems.map((it) => (
                        <option key={it.id} value={it.id}>
                          {it.itemNo}. {it.description.slice(0, 60)}…
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {boqItemId && (
                  <div className="p-2 rounded-lg bg-slate-50 border border-slate-200 text-[11px]">
                    {(() => {
                      const it = boqItems.find((x) => x.id === boqItemId);
                      if (!it) return null;
                      return (
                        <>
                          <div>
                            <b>Item {it.itemNo}</b> – {it.description}
                          </div>
                          <div>
                            <b>Qty:</b> {it.qtyTender} {it.unit || ""} ·{" "}
                            <b>Rate:</b> ₹ {it.rate}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}

                <div>
                  <label className="text-xs text-slate-600 font-semibold">
                    Description / Remarks
                  </label>
                  <textarea
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="mt-1 w-full px-3 py-2 border rounded-lg resize-vertical"
                    placeholder="Work done on this item…"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-600 font-semibold">
                      Qty executed today
                    </label>
                    <input
                      value={qty}
                      onChange={(e) => setQty(e.target.value)}
                      className="mt-1 w-full px-3 py-2 border rounded-lg"
                      placeholder="e.g. 5.5"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-600 font-semibold">
                      Overall Progress (%)
                    </label>
                    <input
                      type="number"
                      value={overallProgress}
                      onChange={(e) => setOverallProgress(e.target.value)}
                      className="mt-1 w-full px-3 py-2 border rounded-lg"
                      placeholder="e.g. 10, 25…"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs text-slate-600 font-semibold">
                    Photos (site) – optional
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileChange}
                    className="mt-1 w-full text-xs"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={cx(
                    "mt-2 px-4 py-2 rounded-lg text-xs font-semibold text-white",
                    loading
                      ? "bg-emerald-300 cursor-not-allowed"
                      : "bg-emerald-600 hover:bg-emerald-700"
                  )}
                >
                  {loading ? "Saving…" : "Save Progress"}
                </button>
              </form>
            </div>

            {/* LIST */}
            <div className="rounded-2xl bg-white border border-emerald-100 p-4 shadow-sm">
              <h2 className="text-sm font-bold text-emerald-800 mb-2">
                Recent Progress Entries
              </h2>
              {logs.length === 0 ? (
                <div className="text-[12px] text-slate-500">
                  No entries yet. Add your first progress entry from the left.
                </div>
              ) : (
                <div className="space-y-3 text-[12px]">
                  {logs.map((log) => {
                    const it = boqItems.find(
                      (x) => x.id === log.boqItemId
                    );
                    return (
                      <div
                        key={log.id}
                        className="border border-slate-200 rounded-xl px-3 py-2 bg-slate-50"
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="font-semibold text-slate-900">
                              {it
                                ? `Item ${it.itemNo}: ${it.description.slice(
                                    0,
                                    40
                                  )}…`
                                : "BOQ Item"}
                            </div>
                            <div className="text-[11px] text-slate-500">
                              {dayjs
                                .unix(log.logDateSeconds || 0)
                                .format("DD/MM/YYYY")}
                            </div>
                          </div>
                          <div className="text-right text-[11px] text-slate-600">
                            <div>
                              <b>Qty:</b> {log.qty} {log.unit || ""}
                            </div>
                            <div>
                              <b>Overall:</b> {log.overallProgress || 0}%
                            </div>
                          </div>
                        </div>
                        {log.description && (
                          <div className="mt-1 text-[11px] text-slate-700">
                            {log.description}
                          </div>
                        )}
                        {Array.isArray(log.photos) &&
                          log.photos.length > 0 && (
                            <div className="mt-1 flex gap-2 flex-wrap">
                              {log.photos.slice(0, 3).map((p, idx) => (
                                <a
                                  key={idx}
                                  href={p.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-block w-16 h-16 rounded-lg overflow-hidden border border-slate-300"
                                >
                                  <img
                                    src={p.url}
                                    alt={p.name || ""}
                                    className="w-full h-full object-cover"
                                  />
                                </a>
                              ))}
                            </div>
                          )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
